import {
  BarChart3,
  BookOpen,
  Dumbbell,
  CalendarDays,
  Ear,
  User,
  ShieldCheck,
  ShieldAlert,
  LogOut,
  Zap,
  Users,
  Flag,
  Search,
  Lightbulb,
  HelpCircle,
  HeartPulse,
  ChevronDown,
  Shield,
  LayoutGrid,
  UserPlus,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState, useMemo, Suspense, useCallback } from "react";
import { isEnabled, type FeatureFlag } from "@/constants/featureFlags";
import { lazyRetry } from "@/lib/lazyRetry";

import { supabase } from "@/integrations/supabase/client";

import { useProfile } from "@/hooks/useProfile";
import { useInterfaceMode, type InterfaceMode } from "@/hooks/useInterfaceMode";
import { useUserIntentProfile } from "@/hooks/useUserIntentProfile";
import { isSectionVisible } from "@/lib/sectionVisibility";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";

const AdminIdeaQuickInput = lazyRetry(() => import("@/components/admin/AdminIdeaQuickInput"));

import { AppliPlusSection } from "@/components/layout/AppliPlusSection";
import { DisplaySettingsPanel } from "@/components/layout/DisplaySettingsPanel";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


function CollapsibleModeSwitchers() {
  const [open, setOpen] = useState(false);
  const { mode } = useInterfaceMode();
  return (
    <div className="mt-2 pt-2 border-t border-sidebar-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md"
      >
        <span className="flex items-center gap-1.5">
          Réglages d'affichage
          {mode === "simple" && (
            <span className="bg-muted text-muted-foreground text-[9px] rounded-full px-1.5 py-0.5 font-medium">
              Mode Simplifié
            </span>
          )}
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1.5">
          <DisplaySettingsPanel variant="compact" />
        </div>
      )}
    </div>
  );
}

// ── Nav items with sectionVisibility keys for filtering ──

const ALL_NAV_ITEMS: {
  title: string;
  url: string;
  icon: typeof BarChart3;
  keywords?: string[];
  footer?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  coachOnly?: boolean;
  healthOnly?: boolean;
  /** sectionVisibility key for centralized filtering */
  sectionKey?: string;
  minMode?: InterfaceMode;
  /** Only show this nav item in simple mode (hidden in core/advanced) */
  simpleOnly?: boolean;
  /** Feature flag gating — item hidden when flag is false */
  featureFlag?: string;
}[] = [
  { title: "Accueil", url: "/dashboard", icon: BarChart3, keywords: ["accueil", "tableau", "activité", "home", "aujourd'hui"] },
  { title: "Programmes", url: "/programs", icon: Dumbbell, keywords: ["programme", "entraînement"], sectionKey: "sidebar.programs" },
  { title: "Communauté", url: "/community", icon: Users, keywords: ["partage", "social", "communauté"], sectionKey: "sidebar.community", minMode: "core" },
  { title: "Bibliothèque", url: "/library", icon: BookOpen, keywords: ["sources", "faits", "science", "bibliothèque"], sectionKey: "sidebar.library", minMode: "advanced" },
  
  { title: "Santé", url: "/health", icon: HeartPulse, keywords: ["santé", "health", "médical", "récupération", "prévention"], healthOnly: true },
  { title: "Modules", url: "/modules", icon: LayoutGrid, keywords: ["modules", "fonctionnalités", "catalogue"], minMode: "advanced", footer: true },
  { title: "Guide", url: "/guide", icon: HelpCircle, keywords: ["guide", "tutoriel", "aide", "help", "comment"], footer: true },
];
function FooterMenuItems({ items }: { items: typeof ALL_NAV_ITEMS }) {
  return (
    <>
      {items.map((item) => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild tooltip={item.title}>
            <NavLink to={item.url} end activeClassName="bg-sidebar-accent text-sidebar-primary font-medium">
              <item.icon className="h-4 w-4" />
              <span className="text-sm">{item.title}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </>
  );
}

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const { profile } = useProfile();
  const { mode, isVisible, isRouteVisibleForIntent } = useInterfaceMode();
  const { profile: intentProfile } = useUserIntentProfile();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isHealthPro, setIsHealthPro] = useState(false);
  const [search, setSearch] = useState("");
  const [ideaOpen, setIdeaOpen] = useState(false);

  const checkRoles = useCallback(() => {
    if (!user) return;
    supabase.rpc("get_user_roles", { _user_id: user.id }).then(({ data }) => {
      if (data) {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        setIsAdmin(parsed.is_admin ?? false);
        setIsSuperAdmin(parsed.is_super_admin ?? false);
        setIsCoach(parsed.is_coach ?? false);
        setIsHealthPro(parsed.is_health_professional ?? false);
      }
    });
  }, [user]);

  useEffect(() => { checkRoles(); }, [checkRoles]);

  // Re-check roles on window focus (catches admin approval without full reload)
  useEffect(() => {
    const onFocus = () => checkRoles();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [checkRoles]);

  const userIntent = intentProfile?.user_intent ?? intentProfile?.primary_goal ?? null;
  const primaryGoal = intentProfile?.primary_goal ?? null;

  // Filter main items using sectionVisibility where available
  const mainItems = useMemo(() =>
    ALL_NAV_ITEMS.filter(item => {
      if (item.footer) return false;
      if (item.coachOnly && !isCoach) return false;
      if (item.healthOnly && !isHealthPro) return false;
      if (item.featureFlag && !isEnabled(item.featureFlag as FeatureFlag)) return false;

      // Items marked simpleOnly are hidden in core/advanced
      if (item.simpleOnly && mode !== "simple") return false;

      // Use centralized sectionVisibility if item has a sectionKey
      if (item.sectionKey) {
        return isSectionVisible(item.sectionKey, mode, userIntent, primaryGoal);
      }

      // Legacy: items with minMode but no sectionKey
      if (item.minMode && !isVisible(item.minMode)) {
        if (mode === "simple") {
          return isRouteVisibleForIntent(item.url, userIntent);
        }
        return false;
      }

      // In simple mode, items without minMode are filtered by intent
      if (mode === "simple" && !item.minMode) {
        return isRouteVisibleForIntent(item.url, userIntent);
      }
      return true;
    }),
    [mode, isVisible, isRouteVisibleForIntent, userIntent, primaryGoal, isCoach, isHealthPro]
  );

  const footerItems = useMemo(() =>
    ALL_NAV_ITEMS.filter(item => {
      if (!item.footer) return false;
      if (item.adminOnly && !isAdmin) return false;
      if (item.superAdminOnly && !isSuperAdmin) return false;
      return true;
    }),
    [isAdmin, isSuperAdmin]
  );

  const searchLower = search.toLowerCase().trim();
  const searchResults = useMemo(() => {
    if (!searchLower) return null;
    return ALL_NAV_ITEMS
      .filter(item => !(item.adminOnly && !isAdmin) && !(item.superAdminOnly && !isSuperAdmin) && !(item.coachOnly && !isCoach) && !(item.healthOnly && !isHealthPro))
      .filter(item => {
        const label = item.title.toLowerCase();
        return label.includes(searchLower) || item.keywords?.some(k => k.includes(searchLower));
      });
  }, [searchLower, isAdmin, isSuperAdmin, isCoach, isHealthPro]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className={collapsed ? "flex items-center justify-center p-2" : "p-4"}>
        <div className={collapsed ? "flex justify-center" : "flex items-center gap-2"}>
          <motion.button
            onClick={() => toggleSidebar()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-primary cursor-pointer"
            whileHover={{ scale: 1.1, rotate: 15 }}
            whileTap={{ scale: 0.9, rotate: -10 }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
            aria-label="Ouvrir/fermer le menu"
            title="Ouvrir/fermer le menu"
          >
            <Zap className="h-6 w-6 text-primary-foreground" />
          </motion.button>
          {!collapsed && (
            <span className="font-display text-2xl font-bold text-foreground flex-1">
              PERF<span className="text-gradient-primary">ORM</span>
            </span>
          )}
          
        </div>
      </SidebarHeader>

      <SidebarContent>
        {!collapsed && (
          <div className="px-3 pt-2 pb-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="h-8 pl-7 text-xs bg-sidebar-accent/50 border-sidebar-border"
              />
            </div>
          </div>
        )}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs uppercase tracking-wider">Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {(searchResults ?? mainItems).map((item) => {
                const label = item.title;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === item.url}
                      tooltip={label}
                    >
                      <NavLink to={item.url} end activeClassName="bg-sidebar-accent text-sidebar-primary font-medium">
                        <item.icon className="h-4 w-4" />
                        <span className="text-sm">{label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              {searchResults !== null && searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground px-3 py-2">Aucun résultat</p>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Idée rapide"
                onClick={() => setIdeaOpen((v) => !v)}
                className={ideaOpen ? "bg-sidebar-accent text-sidebar-primary font-medium" : ""}
              >
                <Lightbulb className="h-4 w-4" />
                <span className="text-sm">Idée</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* Appli + section — pinned modules + catalog */}
          {!collapsed && mode === "advanced" && <AppliPlusSection />}
          {/* Guide footer item */}
          <FooterMenuItems items={footerItems} />

          {/* Administration grouped entry */}
          {(isAdmin || isSuperAdmin) && (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    tooltip="Administration"
                    className={
                      location.pathname.startsWith("/admin") || location.pathname.startsWith("/super-admin")
                        ? "bg-sidebar-accent text-sidebar-primary font-medium"
                        : ""
                    }
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-sm">Administration</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-48">
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/super-admin")}>
                      <ShieldAlert className="h-4 w-4 mr-2" />
                      Super Admin
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}

          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Déconnexion"
              onClick={() => {
                signOut();
                navigate("/");
              }}
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm">Déconnexion</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* Mode switchers — collapsible in footer */}
        {!collapsed && <CollapsibleModeSwitchers />}

      </SidebarFooter>
      <Suspense fallback={null}>
        {isAdmin && <AdminIdeaQuickInput open={ideaOpen} onOpenChange={setIdeaOpen} />}
      </Suspense>
    </Sidebar>
  );
}
